// iOS AR Quick Look renderer.
//
// Apple's AR Quick Look activates when the user taps an anchor of the form
//
//   <a rel="ar" href="model.usdz"><img src="poster.jpg" /></a>
//
// The anchor MUST contain exactly one `<img>` (or `<picture>`) child; any
// other content (text, multiple children) disables AR activation and the
// link just downloads the file instead. We therefore render the human-
// readable button copy as a sibling `<button>` that programmatically clicks
// the (visually hidden) anchor.
//
// URL fragment parameters tune AR Quick Look behaviour. For an estimating
// app we set:
//
//   - allowsContentScaling=0  Locks real-world scale. Critical: the customer
//                             must not be able to shrink the deck to look
//                             smaller than what's been quoted.
//
// We deliberately omit the ecommerce parameters (`applePayButtonType`,
// `checkoutTitle`, `checkoutSubtitle`, `price`, `callToAction`) — those
// surface Apple Pay UI which is wrong for a contractor's preview link.
// The estimate total lives in the footer instead.

import type { SharePayload } from "../types.ts";

const FALLBACK_POSTER = "/sample/hero.svg";

export function renderIosAr(
  // AR Quick Look is impossible without a USDZ, so the caller must have
  // narrowed `usdzUrl` before choosing this branch (see main.ts).
  payload: SharePayload & { usdzUrl: string },
): HTMLElement {
  const container = document.createElement("section");
  container.className = "dm-ar";
  container.setAttribute("data-testid", "dm-ios-ar");

  const usdzWithParams = `${payload.usdzUrl}#allowsContentScaling=0`;

  // The anchor element AR Quick Look hooks into. Apple's docs are strict:
  // exactly one `<img>` or `<picture>` child, nothing else.
  const anchor = document.createElement("a");
  anchor.className = "dm-ar__anchor";
  anchor.setAttribute("rel", "ar");
  anchor.setAttribute("href", usdzWithParams);
  anchor.setAttribute("data-testid", "dm-ar-anchor");

  const poster = document.createElement("img");
  poster.className = "dm-ar__poster";
  poster.src = payload.heroImageUrl ?? FALLBACK_POSTER;
  poster.alt = `AR preview of ${payload.deckName}`;
  poster.loading = "eager";
  poster.decoding = "async";
  anchor.appendChild(poster);

  container.appendChild(anchor);

  // Visible call-to-action. Clicking it forwards to the anchor; AR Quick Look
  // still activates because the synthetic click originates from a user gesture.
  const button = document.createElement("button");
  button.type = "button";
  button.className = "dm-ar__cta";
  button.setAttribute("data-testid", "dm-ar-cta");
  button.textContent = "View in your space";
  button.addEventListener("click", () => {
    anchor.click();
  });
  container.appendChild(button);

  const hint = document.createElement("p");
  hint.className = "dm-ar__hint";
  hint.textContent =
    "Tap to walk around your new deck right where it'll be built. Real-world scale is locked to the quoted dimensions.";
  container.appendChild(hint);

  return container;
}
